import { useEffect, useState, useContext, useMemo, useTransition, useCallback } from 'react'
import { Bolt, Delete, Edit, AddToPhotos, ListAltOutlined } from '@mui/icons-material'
import LockIcon from 'images/lock.svg'
import { CircularProgress, TableCell, TableRow, Checkbox, Tooltip, Fab } from '@mui/material'
import { Tooltip as CustomTooltip } from '@components/tooltip/tooltip'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { Component as HeaderIcon } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Paper, TableCellTooltip } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { listContext, modalContext } from '@contexts'
import { colors } from '@global/constants'
import { useQueryString } from '@global/hooks'
import { api } from 'api'
import { getCellContent } from 'functions'
import { useShiftSelectable } from './hooks/useShiftSelectable'
import { CvsTable } from '../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../components/table/enhanced-table-toolbar'
import * as SnackbarActions from '../../global/snackbar/snackbar.actions'
import { Fuzzy } from '../../utils/fuse-search'
import { t } from '@lingui/core/macro'
import { useAppSelector } from '@redux/hooks'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { useQuery } from '@tanstack/react-query'
import { getLiveblocksPendingSuggestions } from '@global/api/liveblocks'
import { calculateTooltipOffset } from '@admin/template-elements/edit-list/helpers'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: props => ({
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    ...(props.ENABLE_LIVEBLOCKS && {
      '& .MuiTableRow-root': {
        width: '100%',
        tableLayout: 'fixed',
      },
      '& .MuiTableCell-root': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    }),
  }),
  tableWrapper: props => ({
    overflowX: 'auto',
    width: '100%',
    ...(props.ENABLE_LIVEBLOCKS && {
      '& .MuiTable-root': {
        width: '10%',
      },
    }),
  }),
  textField: {
    width: 200,
  },
  poweredIcon: {
    marginLeft: '-1.25rem',
  },
  listTypeCell: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
  },
}))

export const Lists = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const ModalContext = useContext(modalContext)
  const { byId, fetchLists, updateLists } = useContext(listContext)
  const [loaded, setLoaded] = useState(false)
  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(history.location.search).get('q') || '',
  )
  const [selectedContainsPoweredTemplate, setSelectedContainsPoweredTemplate] = useState(false)
  const lists = useMemo(() => Object.keys(byId).map(id => byId[id]), [byId])
  const { setUrl } = useQueryString(searchTerm, setSearchTerm)
  const [isPending] = useTransition()

  const [ENABLE_LIVEBLOCKS = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.ENABLE_LIVEBLOCKS]),
  )

  const permissions = useAppSelector(state => state.Auth.permissions)
  const isApproverMode = ENABLE_LIVEBLOCKS ? Boolean(permissions?.approve_suggestions) : true

  const { data: pendingSuggestionsData = [] } = useQuery({
    queryKey: ['liveblocks', 'pending-suggestions'],
    queryFn: getLiveblocksPendingSuggestions,
    enabled: ENABLE_LIVEBLOCKS,
  })

  const pendingSuggestionsMap = useMemo(() => {
    return Object.fromEntries(
      pendingSuggestionsData.map(({ listId, pendingSuggestions, lockData }) => [
        listId,
        { pendingSuggestions, lockData },
      ]),
    )
  }, [pendingSuggestionsData])

  const columns = useMemo(() => {
    if (ENABLE_LIVEBLOCKS) {
      return [
        { label: t`Name`, width: '30%', lookup: 'name' },
        { label: t`Updated Date`, width: '15%', lookup: 'updated_at' },
        { label: t`Type`, width: '10%', lookup: 'type' },
        { label: t`Pending Suggestions`, width: '15%', lookup: 'pending_suggestions' },
        { label: t`Lock Status`, width: '10%', lookup: 'lock_status' },
        { label: t`Description`, width: '20%', lookup: 'notes' },
      ]
    }
    return [
      { label: t`Name`, width: '40%', lookup: 'name' },
      { label: t`Updated Date`, width: '20%', lookup: 'updated_at' },
      { label: t`Type`, width: '20%', lookup: 'type' },
      { label: t`Description`, width: '20%', lookup: 'notes' },
    ]
  }, [ENABLE_LIVEBLOCKS])

  const { selected, setSelected, handleRowClick, numberOfSelected } = useShiftSelectable()

  useEffect(() => {
    setSelectedContainsPoweredTemplate(false)
    const selectedListsThatPowerTemplate = lists.filter(
      l => selected[l.id] && l.powered_template_id,
    )
    if (selectedListsThatPowerTemplate.length > 0) {
      setSelectedContainsPoweredTemplate(true)
    }
  }, [selected])

  const fuzzy = useMemo(() => new Fuzzy(lists, { keys: ['name'] }), [lists])

  useEffect(() => {
    setLoaded(false)
    fetchLists().then(() => setLoaded(true))
  }, [fetchLists])

  const displayLists = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return lists
    }

    return fuzzy.search(searchTerm)
  }, [lists, searchTerm, fuzzy])

  const filterChanged = useCallback(search => setUrl({ q: search }))

  const duplicateItem = () => {
    const edit = Object.keys(selected)[0]
    history.push(`/admin/lists/edit/new?duplicate=${edit}`)
  }

  const onReset = () => {}

  const editItems = () => {
    const edit = Object.keys(selected)[0]
    history.push(`/admin/lists/edit/${edit}`)
  }

  const addItem = () => {
    history.push(`/admin/lists/edit/new`)
  }

  const deleteItems = payload => {
    const filteredLists = lists.filter(l => !payload[l.id])
    api
      .post('/api/v1/account/lists/bulk/delete', {
        list_ids: Object.keys(payload).filter(p => payload[p]),
      })
      .then(() => {
        updateLists(
          filteredLists.reduce((memo, item) => {
            memo[item.id] = item
            return memo
          }, {}),
        )
        setSelected({})
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Pick List deleted successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Pick List failed to delete`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete these Pick Lists?`,
      description: t`Pick Lists are an important part of the Claravine experience and submission templates may depend on them. Please be sure this is what you'd like to do as this action can't be undone.`,
      confirm: () => () => {
        deleteItems(selected)
      },
    })
  }

  const navigateToList = list => {
    history.push(`/admin/lists/edit/${list.id}`)
  }

  const deleteTooltipTitle = selectedContainsPoweredTemplate
    ? t`This field is being used in a template that powers a list. To delete, please first unpower the associated list.`
    : t`Delete`

  const lockStatusTooltipMessage = isApproverMode
    ? t`This pick list is locked while pending suggestions are under review by another approver. Suggesters can’t make new comments or suggestions, and no one can add, remove, or edit columns or rows.`
    : t`This pick list is locked while pending suggestions are under review. Once all suggestions are resolved, new comments and suggestions can be added.`

  const classes = useStyles({ ENABLE_LIVEBLOCKS })

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
      data-test-page-id="admin-lists"
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Pick lists`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Collections of options for users to select from when creating submissions`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper elevation={2} className={classes.paper}>
        {loaded ? (
          <div className={classes.table} style={{ height: 'calc(100% - 105px)' }}>
            {/* hide 'Add new list' button for non-approvers who have access to Pick Lists with noAdd prop */}
            <EnhancedTableToolbar
              term={searchTerm}
              numSelected={numberOfSelected}
              filterChanged={filterChanged}
              addItem={addItem}
              editItems={editItems}
              deleteItems={deleteItems}
              noAdd={!isApproverMode}
              onReset={onReset}
              blockMultipleEdit={false}
              duplicateItem={duplicateItem}
            >
              <Tooltip title={t`Duplicate`}>
                <Fab
                  style={{
                    color: 'white',
                    marginRight: '10px',
                  }}
                  color="secondary"
                  size="small"
                  aria-label={t`Duplicate`}
                  className={classes.fab}
                  disabled={numberOfSelected > 1}
                  onClick={duplicateItem}
                >
                  <AddToPhotos />
                </Fab>
              </Tooltip>
              <Tooltip title={t`Edit`}>
                <Fab
                  color="primary"
                  style={{ color: 'white', marginRight: '10px' }}
                  size="small"
                  aria-label={t`Add`}
                  className={classes.fab}
                  disabled={numberOfSelected > 1}
                  onClick={editItems}
                >
                  <Edit />
                </Fab>
              </Tooltip>
              <Tooltip title={deleteTooltipTitle}>
                <span>
                  <Fab
                    color="inherit"
                    size="small"
                    aria-label={t`Add`}
                    className={classes.fab}
                    onClick={deleteItemsModal}
                    style={{
                      backgroundColor: selectedContainsPoweredTemplate ? '' : colors.red.light,
                      color: 'white',
                    }}
                    disabled={selectedContainsPoweredTemplate}
                  >
                    <Delete />
                  </Fab>
                </span>
              </Tooltip>
            </EnhancedTableToolbar>
            <div style={{ height: 'calc(100% - 64px)' }}>
              <CvsTable
                isLoading={isPending}
                columns={columns}
                rows={displayLists}
                orderBy="updated_at"
                sortOrder="desc"
                keyField="id"
                noAdd={false}
                addItem={addItem}
                noPagination
                style={
                  ENABLE_LIVEBLOCKS ? { maxWidth: '100%', width: '100%' } : { maxWidth: '100%' }
                }
                multiSelect
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.light,
                        cursor: 'auto',
                      }}
                      disabled
                    >
                      <ListAltOutlined style={{ fontSize: '34px', color: colors.blue.smokey }} />
                    </Fab>
                  ),
                  title: t`No Lists Found.`,
                  description: t`Try a different search or add a new list.`,
                }}
                selectAll={val => {
                  setSelected(
                    displayLists.reduce(
                      (memo, item) => ({
                        ...memo,
                        [item.id]: val,
                      }),
                      {},
                    ),
                  )
                }}
              >
                {({ row }) => (
                  <TableRow
                    hover
                    onClick={e => {
                      e.preventDefault()
                      numberOfSelected
                        ? handleRowClick(row, displayLists, e.shiftKey)
                        : navigateToList(row)
                    }}
                    tabIndex={-1}
                    key={row.id}
                    style={
                      ENABLE_LIVEBLOCKS
                        ? { cursor: 'pointer', width: '100%', tableLayout: 'fixed' }
                        : { cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }
                    }
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        data-test-page-id={row.name}
                        onClick={e => {
                          e.stopPropagation()
                          handleRowClick(row, displayLists, e.shiftKey)
                        }}
                        checked={selected[row.id]}
                      />
                    </TableCell>
                    <TableCellTooltip title={row.name}>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.name}
                      </TableCell>
                    </TableCellTooltip>
                    <TableCellTooltip title={row.date}>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getCellContent('date', row.updated_at)}
                      </TableCell>
                    </TableCellTooltip>
                    <TableCellTooltip title={row.listTypeCell}>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        className={classes.listTypeCell}
                      >
                        {row.powered_template_id && (
                          <Bolt className={classes.poweredIcon} fontSize="small" />
                        )}
                        {getCellContent('listType', row.type)}
                      </TableCell>
                    </TableCellTooltip>
                    {ENABLE_LIVEBLOCKS && (
                      <>
                        <TableCell
                          component="th"
                          scope="row"
                          padding="normal"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {pendingSuggestionsMap[row.id]?.pendingSuggestions ? (
                            <span
                              style={{
                                background: 'rgba(62, 143, 247, 0.20)',
                                borderRadius: '12px',
                                minWidth: '33px',
                                minHeight: '16px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {pendingSuggestionsMap[row.id].pendingSuggestions}
                            </span>
                          ) : (
                            ''
                          )}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          padding="normal"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            paddingTop: '10px',
                            paddingBottom: '10px',
                          }}
                        >
                          {pendingSuggestionsMap[row.id]?.lockData?.isLocked ? (
                            <CustomTooltip
                              placement="bottom"
                              title={lockStatusTooltipMessage}
                              className="text-center"
                              mode="light"
                              modifyPosition={calculateTooltipOffset}
                            >
                              <LockIcon />
                            </CustomTooltip>
                          ) : (
                            ''
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCellTooltip title={row.notes}>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getCellContent('text', row.notes)}
                      </TableCell>
                    </TableCellTooltip>
                  </TableRow>
                )}
              </CvsTable>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        )}
      </Paper>
    </motion.div>
  )
}
